import { services as s } from "@/services";
import type { EntryViewT } from "@patient-watch/types";
import dayjs from "dayjs";
import { DEFAULT_LOCALE, formatAssessmentLabel, type SupportedLocale } from "@patient-watch/i18n";

import { isScheduledEntry } from "./buildEntryScheduleSlots";
import { resolveScheduleStepLabel } from "./timingLabelDerivation";

/** Matches the "Entry for Week 1" line on ePRO form pages. */
export const entryScheduleLabelClassName =
    "font-bold text-teal-600 dark:text-teal-400 text-xs sm:text-sm text-left uppercase leading-snug tracking-wider";

/** Date used for sorting and the main header on history cards. */
export function historyDateForEntry(entry: EntryViewT): string | null | undefined {
    if (isScheduledEntry(entry)) {
        return entry.calculated_entry_time;
    }
    return entry.entry_time ?? entry.calculated_entry_time;
}

export function formatHistoryDate(entry: EntryViewT): string {
    const value = historyDateForEntry(entry);
    return value ? s.time.formatShortDateTime(value) : "No date";
}

/** Step label from reference date (for extra / unscheduled entries). */
export function relativeStepLabelFromReference(
    referenceDate: string | null | undefined,
    at: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string | null {
    if (!referenceDate || !at) return null;

    const ref = dayjs(referenceDate);
    const point = dayjs(at);
    const diffDays = point.diff(ref, "day");

    if (diffDays < 0) return null;
    if (diffDays === 0) {
        if (locale === "de-DE") return "Ausgangswert";
        if (locale === "da-DK") return "Udgangspunkt";
        if (locale === "fr-FR") return "Valeur initiale";
        if (locale === "it-IT") return "Valore iniziale";
        if (locale === "es-ES") return "Valor inicial";
        return "Baseline";
    }
    if (diffDays < 14) {
        if (locale === "de-DE") return `Tag ${diffDays}`;
        if (locale === "da-DK") return `Dag ${diffDays}`;
        if (locale === "fr-FR") return `Jour ${diffDays}`;
        if (locale === "it-IT") return `Giorno ${diffDays}`;
        if (locale === "es-ES") return `Día ${diffDays}`;
        return `Day ${diffDays}`;
    }

    const diffWeeks = point.diff(ref, "week");
    if (diffWeeks < 12) {
        if (locale === "de-DE") return `Woche ${diffWeeks}`;
        if (locale === "da-DK") return `Uge ${diffWeeks}`;
        if (locale === "fr-FR") return `Semaine ${diffWeeks}`;
        if (locale === "it-IT") return `Settimana ${diffWeeks}`;
        if (locale === "es-ES") return `Semana ${diffWeeks}`;
        return `Week ${diffWeeks}`;
    }

    const diffMonths = point.diff(ref, "month");
    if (locale === "de-DE") return `Monat ${diffMonths}`;
    if (locale === "da-DK") return `Måned ${diffMonths}`;
    if (locale === "fr-FR") return `Mois ${diffMonths}`;
    if (locale === "it-IT") return `Mese ${diffMonths}`;
    if (locale === "es-ES") return `Mes ${diffMonths}`;
    return `Month ${diffMonths}`;
}

/** Answers to a screener, given before sign-up and saved onto the diary it led to. */
export function isScreenerEntry(entry: Pick<EntryViewT, "submission_source">): boolean {
    return entry.submission_source === "screener";
}

function screenerLabel(locale: SupportedLocale): string {
    if (locale === "de-DE") return "Screening";
    if (locale === "da-DK") return "Screening";
    if (locale === "fr-FR") return "Présélection";
    if (locale === "it-IT") return "Screening";
    if (locale === "es-ES") return "Cribado";
    return "Screener";
}

/** Resolve a display label from stored or derived timing metadata. */
export function resolvedTimingLabel(entry: EntryViewT, locale: SupportedLocale = DEFAULT_LOCALE): string {
    // A screener comes before the diary's schedule, so no timing applies to it.
    if (isScreenerEntry(entry)) return screenerLabel(locale);

    const explicit = entry.timing_label?.trim();
    if (explicit) {
        return resolveScheduleStepLabel({ timing_label: explicit }, { locale }) ?? explicit;
    }

    const relative = relativeStepLabelFromReference(
        entry.entry_reference_date,
        entry.calculated_entry_time ?? entry.entry_time,
        locale
    );
    if (relative) {
        return relative;
    }

    const fromSchedule = resolveScheduleStepLabel(
        {
            timing_interval: entry.timing_interval,
            time_unit: entry.time_unit,
            schedule_index: entry.schedule_index,
        },
        { locale }
    );
    if (fromSchedule) {
        return fromSchedule;
    }

    if (isScheduledEntry(entry) && entry.schedule_index != null) {
        return formatAssessmentLabel(entry.schedule_index + 1, locale);
    }

    if (locale === "de-DE") return "Zusätzlicher Eintrag";
    if (locale === "da-DK") return "Ekstra indtastning";
    if (locale === "fr-FR") return "Saisie supplémentaire";
    if (locale === "it-IT") return "Inserimento aggiuntivo";
    if (locale === "es-ES") return "Registro adicional";
    return "Extra entry";
}

/** Primary history card title (Week 10, Day 3, Baseline, etc.). */
export function historyStepLabel(entry: EntryViewT, locale: SupportedLocale = DEFAULT_LOCALE): string {
    return resolvedTimingLabel(entry, locale);
}

/** Muted subtitle when the patient submitted the entry (record date). */
export function submittedDateSubtitle(entry: EntryViewT): string | null {
    if (!entry.entry_time) return null;
    return `Submitted ${dayjs(entry.entry_time).format("D MMM YYYY")}`;
}
